import React, { useState, useCallback, useMemo, useRef } from 'react';
import { ClassNameMap } from '@mui/styles';
import TextField from '@mui/material/TextField';
import Autocomplete, {
  AutocompleteRenderInputParams
} from '@mui/material/Autocomplete';
import { Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useIntl } from 'react-intl';
import { useDebounce } from 'react-use';
import { Controller, useFormContext } from 'react-hook-form';
import { InputLabelProps } from '@mui/material/InputLabel';
import { GroupSearchResult } from 'graphql/types';
import { mapToUiAccessSope } from './utils';
import { useMoreMessage } from './hooks/useMoreMessage';
import { SelectHierarchyOptionItem } from './SelectHierarchyOption';

const defaultAutoCompleteWidth = 28;

const useStyles = makeStyles((theme) => ({
  autocompleteDialog: {
    width: theme.spacing(defaultAutoCompleteWidth + 2),
    margin: theme.spacing(1),
    paddingRight: theme.spacing(0),
    '&.MuiAutocomplete-hasPopupIcon.css-gtwism-MuiAutocomplete-root .MuiOutlinedInput-root': {
      paddingRight: theme.spacing(2.5),
      minWidth: theme.spacing(0)
    },
    '& .MuiAutocomplete-popper': {
      minWidth: theme.spacing(defaultAutoCompleteWidth + 2)
    },
    '&.MuiAutocomplete-root .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
      minWidth: theme.spacing(0)
    }
  },
  chip: {
    minWidth: theme.spacing(2),
    maxWidth: theme.spacing(15.5),
    marginLeft: theme.spacing(0.4)
  },
  labelTooltip: {
    '&.MuiTooltip-popper .MuiTooltip-tooltip': {
      margin: theme.spacing(3, 0, 0, -3)
    }
  }
}));

const useInputLabelStyles = makeStyles(() => ({
  root: {
    maxWidth: 'calc(100% - 35px)'
  },
  focused: {
    maxWidth: 'calc(133% - 40px)'
  }
}));

type GroupSelectProp = {
  groupType: string;
  handleSearch: (groupType: string, queryText: string | null) => void;
  onOpen: () => void;
  options: GroupSearchResult[];
  hasMore: boolean;
  loading: boolean;
  rowID: string;
  defaultValues: any[];
  classes?: ClassNameMap;
};

export const GroupSelect: React.FC<GroupSelectProp> = ({
  groupType,
  handleSearch,
  onOpen,
  options,
  hasMore,
  loading,
  rowID,
  defaultValues,
  classes: overrideClasses
}: GroupSelectProp) => {
  const [search, setSearch] = useState('');
  const searching = useRef(false);

  const classes = useStyles({
    classes: overrideClasses
  });
  const inputLabelClasses = useInputLabelStyles();
  const message = useMoreMessage(groupType);
  const autoCompleteRef = useRef<HTMLDivElement>(null);
  const [autoCompleteWidth, setAutoCompleteWidth] = useState(
    defaultAutoCompleteWidth
  );
  const { formatMessage } = useIntl();
  const { control } = useFormContext();
  const groupSelectID = useMemo(() => `${rowID}.${groupType}`, [
    groupType,
    rowID
  ]);

  const handleInputChange = useCallback((event, value) => {
    searching.current = true;
    setSearch(value);
  }, []);

  useDebounce(
    () => {
      if (searching.current) handleSearch(groupType, search);
    },
    500,
    [search]
  );

  const renderOption = useCallback((props, option, { selected }) => {
    return (
      <li {...props} key={props.id}>
        <SelectHierarchyOptionItem
          hierarchyLevel={option.level}
          title={option.displayText}
          selected={selected}
        />
      </li>
    );
  }, []);
  const filterOptions = useCallback(
    (options) => {
      hasMore && options.push(message);
      return options as GroupSearchResult[];
    },
    [hasMore, message]
  );

  const getOptionSelected = useCallback((option, value) => {
    return option.id === value.id;
  }, []);

  const loadingText = useMemo(() => {
    return (
      <Typography data-testid="auto-complete-loading">
        {formatMessage({ id: 'settings.loading' })}
      </Typography>
    );
  }, [formatMessage]);

  const label = useMemo(() => {
    return formatMessage({ id: `settings.groups.single.${groupType}` });
  }, [formatMessage, groupType]);

  const floatingLabelText = useMemo(() => {
    return label.length > autoCompleteWidth - 3
      ? label.substring(0, autoCompleteWidth - 3) + '...'
      : label;
  }, [autoCompleteWidth, label]);

  const noOptionText = useMemo(() => {
    return formatMessage({ id: `settings.groups.no.${groupType}` });
  }, [formatMessage, groupType]);

  const setAutoCompleteFieldWidth = useCallback((data) => {
    autoCompleteRef.current && data.length > 1
      ? setAutoCompleteWidth(
          autoCompleteRef.current.getBoundingClientRect().width / 8
        )
      : setAutoCompleteWidth(defaultAutoCompleteWidth);
  }, []);

  const toolTipTitle = useMemo(
    () => (floatingLabelText && floatingLabelText.endsWith('...') ? label : ''),
    [floatingLabelText, label]
  );

  const textFieldLabel = useMemo(() => {
    return (
      <Tooltip
        title={toolTipTitle}
        PopperProps={{ className: classes.labelTooltip }}
        placement="bottom-start"
      >
        <span>{floatingLabelText}</span>
      </Tooltip>
    );
  }, [classes.labelTooltip, floatingLabelText, toolTipTitle]);

  const inputProps = useCallback(
    (params) => ({
      ...params.InputProps,
      label: floatingLabelText
    }),
    [floatingLabelText]
  );

  const handleRenderTags = useCallback(
    (tagValue, getTagProps) => {
      return tagValue.map((option: GroupSearchResult, index: number) => {
        return (
          <>
            <Tooltip
              key={option.id}
              title={option.displayText}
              placement={'top'}
            >
              <Typography
                className={classes.chip}
                noWrap
                data-testid={`option-${option.displayText}`}
                {...getTagProps({ index })}
              >
                {`${option.displayText}`}
              </Typography>
            </Tooltip>
            {index < tagValue.length - 1 ? (
              <span>,&nbsp;</span>
            ) : (
              <span>&nbsp;</span>
            )}
          </>
        );
      });
    },
    [classes.chip]
  );
  const handleOnBlur = useCallback(() => {
    setAutoCompleteWidth(defaultAutoCompleteWidth);
  }, []);

  const onChangeHandler = useCallback(
    (onChange) => (
      event: React.ChangeEvent<any>,
      data: GroupSearchResult[]
    ) => {
      setAutoCompleteFieldWidth(data);

      const newData = mapToUiAccessSope(data);
      onChange(newData);
    },
    [setAutoCompleteFieldWidth]
  );

  const inputLabelProps = useMemo<InputLabelProps>(
    () => ({
      classes: inputLabelClasses
    }),
    [inputLabelClasses]
  );

  const getInputComponent = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        InputProps={inputProps(params)}
        variant="outlined"
        label={textFieldLabel}
        placeholder=""
        data-testid={`simple-Search-${groupSelectID}`.toLowerCase()}
        InputLabelProps={inputLabelProps}
      />
    ),
    [inputProps, textFieldLabel, groupSelectID, inputLabelProps]
  );

  return (
    <Controller
      render={({ field: { onChange, ...props } }) => (
        <Autocomplete
          multiple
          data-testid={`setting-autocomplete-${groupType}`.toLowerCase()}
          limitTags={1}
          loading={loading}
          loadingText={loadingText}
          renderOption={renderOption}
          isOptionEqualToValue={getOptionSelected}
          inputValue={search}
          onOpen={onOpen}
          filterOptions={filterOptions}
          onInputChange={handleInputChange}
          size="small"
          options={options}
          disableCloseOnSelect
          getOptionLabel={(option) => option.displayText}
          className={classes.autocompleteDialog}
          noOptionsText={noOptionText}
          renderTags={handleRenderTags}
          renderInput={getInputComponent}
          onChange={onChangeHandler(onChange)}
          {...props}
          ref={autoCompleteRef}
          onBlur={handleOnBlur}
        />
      )}
      name={groupSelectID}
      control={control}
      defaultValue={defaultValues}
    />
  );
};
